import {
	MfaMethodEnum,
	useRegisterMutation,
	useValidateAuthenticationTokenMutation,
} from '@codezo/graphql';
import { useIsDarkMode, useThemeColors } from '@codezo/theme';
import {
	Alert,
	Anchor,
	Box,
	Button,
	Center,
	Group,
	Loader,
	Paper,
	PasswordInput,
	SegmentedControl,
	Stack,
	Text,
	TextInput,
	Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
	IconAlertCircle,
	IconMail as IconEmailMfa,
	IconLock,
	IconMail,
	IconShield,
	IconCheck,
	IconX,
	IconMinus,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/common/Logo';

// Password regex: 8-32 chars, must have lowercase, uppercase, number, and special char
export const passwordRegex =
	/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#_-])[A-Za-z\d@$!%*?&#_-]{8,32}$/;

// Password requirement checker
function checkPasswordRequirements(password: string) {
	return {
		length: password.length >= 8 && password.length <= 32,
		uppercase: /[A-Z]/.test(password),
		lowercase: /[a-z]/.test(password),
		number: /\d/.test(password),
		special: /[@$!%*?&#_-]/.test(password),
	};
}

export function RegisterPage() {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [mfaMethod, setMfaMethod] = useState<MfaMethodEnum>(MfaMethodEnum.Authenticator);
	const [error, setError] = useState<string | null>(null);
	const [isValidatingToken, setIsValidatingToken] = useState(true);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const colors = useThemeColors();
	const isMobile = useMediaQuery('(max-width: 48em)');
	const isDark = useIsDarkMode();

	// Get invitation token if provided
	const inviteToken = searchParams.get('token');

	const passwordsMatch = password === confirmPassword && password.length > 0;
	const isPasswordValid = passwordRegex.test(password);

	// Check password requirements in real-time
	const passwordRequirements = useMemo(() => checkPasswordRequirements(password), [password]);

	// Validate token and get user email
	const [validateToken] = useValidateAuthenticationTokenMutation({
		onCompleted: (data) => {
			if (data.validateAuthenticationToken?.user?.email) {
				setEmail(data.validateAuthenticationToken.user.email);
			}
			setIsValidatingToken(false);
		},
		onError: () => {
			setError('Invalid or expired invitation token');
			setIsValidatingToken(false);
		},
	});

	useEffect(() => {
		if (inviteToken) {
			validateToken({
				variables: {
					token: inviteToken,
				},
			});
		} else {
			setIsValidatingToken(false);
		}
	}, [inviteToken, validateToken]);

	const [register, { loading }] = useRegisterMutation({
		onCompleted: (data) => {
			// Removed console.log
			if (data.register?.userId) {
				if (mfaMethod === MfaMethodEnum.Authenticator && data.register.qrCodeDataURL) {
					// Navigate to MFA setup for authenticator with QR code
					navigate('/auth/register/mfa', {
						state: {
							userId: data.register.userId,
							qrCodeDataURL: data.register.qrCodeDataURL,
						},
					});
				} else {
					// For email MFA, go directly to email verification page
					// No token needed for registration flow
					navigate('/auth/mfa/email-verify', {
						state: { userId: data.register.userId },
					});
				}
			}
		},
		onError: (err) => {
			setError(err.message || 'Registration failed. Please try again.');
		},
	});

	const handleRegister = async () => {
		if (!inviteToken || !email || !passwordsMatch || !isPasswordValid) return;

		setError(null);

		try {
			await register({
				variables: {
					input: {
						token: inviteToken,
						password,
						mfaMethod,
					},
				},
			});
		} catch {
			// Error is already handled by onError callback
			// Error handled by mutation onError
		}
	};

	if (isValidatingToken) {
		return (
			<Center h="100vh">
				<Loader color="codezo" size="lg" />
			</Center>
		);
	}

	if (!inviteToken) {
		return (
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					p={{ base: 'xs', sm: 'xl' }}
					radius="md"
					miw={{ base: 'calc(100vw - 20px)', xs: 480 }}
				>
					<Stack gap="lg" align="center">
						<Alert
							icon={<IconAlertCircle size={16} />}
							color="error"
							style={{ width: '100%' }}
						>
							Invalid or missing invitation token. Please use the link from your
							invitation email.
						</Alert>
						<Button component={Link} to="/auth/login" variant="subtle">
							Back to login
						</Button>
					</Stack>
				</Paper>
			</motion.div>
		);
	}

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					p={{ base: 'xs', sm: 'xl' }}
					radius="md"
					miw={{ base: 'calc(100vw - 20px)', xs: 480 }}
				>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={40} />
							<div>
								<Title mb={4}>Create Your Account</Title>
								<Text size="sm" c={colors.textMuted}>
									Set up your 05 account with secure authentication
								</Text>
							</div>
						</Stack>

						{error && (
							<Alert
								icon={<IconAlertCircle size={20} color={colors.error} />}
								color="error"
							>
								{error}
							</Alert>
						)}

						<Stack gap="md">
							<TextInput
								label="Email"
								placeholder="your@email.com"
								leftSection={<IconMail size={18} />}
								value={email}
								readOnly
								disabled
							/>

							<Box>
								<PasswordInput
									label="Password"
									placeholder="Create a strong password"
									leftSection={<IconLock size={18} />}
									value={password}
									onChange={(e) => setPassword(e.currentTarget.value)}
									disabled={loading}
									required
								/>
								<Stack gap={4} mt={8}>
									<Group gap={6}>
										{(() => {
											if (password && passwordRequirements.length) {
												return (
													<IconCheck
														size={14}
														style={{
															color: 'var(--mantine-color-teal-6)',
														}}
													/>
												);
											}
											if (password) {
												return (
													<IconX
														size={14}
														style={{
															color: 'var(--mantine-color-red-6)',
														}}
													/>
												);
											}
											return (
												<IconMinus
													size={14}
													style={{ color: 'var(--mantine-color-gray-5)' }}
												/>
											);
										})()}
										<Text size="xs">8-32 characters long</Text>
									</Group>
									<Group gap={6}>
										{(() => {
											if (password && passwordRequirements.uppercase) {
												return (
													<IconCheck
														size={14}
														style={{
															color: 'var(--mantine-color-teal-6)',
														}}
													/>
												);
											}
											if (password) {
												return (
													<IconX
														size={14}
														style={{
															color: 'var(--mantine-color-red-6)',
														}}
													/>
												);
											}
											return (
												<IconMinus
													size={14}
													style={{ color: 'var(--mantine-color-gray-5)' }}
												/>
											);
										})()}
										<Text size="xs">Contains uppercase letter</Text>
									</Group>
									<Group gap={6}>
										{(() => {
											if (password && passwordRequirements.lowercase) {
												return (
													<IconCheck
														size={14}
														style={{
															color: 'var(--mantine-color-teal-6)',
														}}
													/>
												);
											}
											if (password) {
												return (
													<IconX
														size={14}
														style={{
															color: 'var(--mantine-color-red-6)',
														}}
													/>
												);
											}
											return (
												<IconMinus
													size={14}
													style={{ color: 'var(--mantine-color-gray-5)' }}
												/>
											);
										})()}
										<Text size="xs">Contains lowercase letter</Text>
									</Group>
									<Group gap={6}>
										{(() => {
											if (password && passwordRequirements.number) {
												return (
													<IconCheck
														size={14}
														style={{
															color: 'var(--mantine-color-teal-6)',
														}}
													/>
												);
											}
											if (password) {
												return (
													<IconX
														size={14}
														style={{
															color: 'var(--mantine-color-red-6)',
														}}
													/>
												);
											}
											return (
												<IconMinus
													size={14}
													style={{ color: 'var(--mantine-color-gray-5)' }}
												/>
											);
										})()}
										<Text size="xs">Contains a number</Text>
									</Group>
									<Group gap={6}>
										{(() => {
											if (password && passwordRequirements.special) {
												return (
													<IconCheck
														size={14}
														style={{
															color: 'var(--mantine-color-teal-6)',
														}}
													/>
												);
											}
											if (password) {
												return (
													<IconX
														size={14}
														style={{
															color: 'var(--mantine-color-red-6)',
														}}
													/>
												);
											}
											return (
												<IconMinus
													size={14}
													style={{ color: 'var(--mantine-color-gray-5)' }}
												/>
											);
										})()}
										<Text size="xs">
											Contains special character (@$!%*?&#_-)
										</Text>
									</Group>
								</Stack>
							</Box>

							<PasswordInput
								label="Confirm Password"
								placeholder="Confirm your password"
								leftSection={<IconLock size={18} />}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.currentTarget.value)}
								disabled={loading}
								error={
									confirmPassword && !passwordsMatch
										? "Passwords don't match"
										: undefined
								}
								required
							/>

							<Stack gap="xs">
								<Text size="sm" fw={500} c={colors.textMuted}>
									Two-Factor Authentication Method
								</Text>
								<SegmentedControl
									value={mfaMethod}
									onChange={(value) => setMfaMethod(value as MfaMethodEnum)}
									data={[
										{
											label: isMobile ? (
												<Stack gap={4} align="center">
													<IconShield size={16} />
													<Text size="xs">Authenticator App</Text>
												</Stack>
											) : (
												<Group gap="xs">
													<IconShield size={16} />
													<Text size="sm">Authenticator App</Text>
												</Group>
											),
											value: MfaMethodEnum.Authenticator,
										},
										{
											label: isMobile ? (
												<Stack gap={4} align="center">
													<IconEmailMfa size={16} />
													<Text size="xs">Email</Text>
												</Stack>
											) : (
												<Group gap="xs">
													<IconEmailMfa size={16} />
													<Text size="sm">Email Code</Text>
												</Group>
											),
											value: MfaMethodEnum.Email,
										},
									]}
								/>
								<Text size="xs" c={colors.textMuted}>
									{mfaMethod === MfaMethodEnum.Authenticator
										? "You'll need an authenticator app like Google Authenticator or Authy"
										: 'We&apos;ll send verification codes to your email address'}
								</Text>
							</Stack>
						</Stack>

						<Button
							fullWidth
							onClick={handleRegister}
							loading={loading}
							disabled={!email || !passwordsMatch || !isPasswordValid}
							size="sm"
						>
							Create Account
						</Button>

						<Group justify="center">
							<Text size="sm" c={colors.textMuted}>
								Already have an account?{' '}
								<Anchor component={Link} to="/auth/login">
									Sign in
								</Anchor>
							</Text>
						</Group>
					</Stack>
				</Paper>

				{/* Background decoration */}
				<Box
					style={{
						position: 'fixed',
						top: '-20%',
						right: '-10%',
						width: '600px',
						height: '600px',
						background: isDark
							? 'radial-gradient(circle, rgba(6, 112, 86, 0.15) 0%, transparent 70%)'
							: 'radial-gradient(circle, rgba(6, 112, 86, 0.1) 0%, transparent 70%)',
						filter: 'blur(100px)',
						zIndex: -1,
					}}
				/>
			</motion.div>
		</Box>
	);
}
